import { produce } from 'immer';
import * as actions from '../constants/action-types';
import dummyCandidate from './dummyCandidate.json';

const sortByNewest = (candidates) => candidates.sort((prevKey, nextKey) => nextKey - prevKey);
const sortByOldest = (candidates) => candidates.sort((prevKey, nextKey) => prevKey - nextKey);

const transformFilterData = (data) =>
	data.map(({ name, id }) => ({
		name,
		value: id,
	}));

const transformTagsFilterData = (data) =>
	data.map(({ tag_name, tag_id, hex_colour_bg, hex_colour_text }) => ({
		name: tag_name,
		value: tag_id,
		bgColor: hex_colour_bg,
		textColor: hex_colour_text,
		isChip: true,
	}));

export default (state = {}, { type, payload }) =>
	produce(state, (draft) => {
		switch (type) {
			case actions.GET_CANDIDATES:
				draft.isFiltered = payload.isFiltered;
				draft.candidatesRequestId = payload.requestId;
				draft.failedCandidatesRequest = null;

				if (payload.start === 0) {
					draft.loadingCandidates = true;
					draft.loadingMoreCandidates = false;
				} else {
					draft.loadingCandidates = false;
					draft.loadingMoreCandidates = true;
				}

				break;

			case actions.GET_CANDIDATES_FAILED:
				draft.loadingCandidates = false;
				draft.loadingMoreCandidates = false;
				draft.failedCandidatesRequest = payload.retryArgs;
				break;

			case actions.REFRESH_CANDIDATES_WITH_FILTERS:
				draft.loadingCandidates = true;
				draft.loadingMoreCandidates = false;
				break;

			case actions.SET_CANDIDATE_FILTERS:
				draft.currentFilters = payload.filters;
				draft.currentSearchValue = payload.searchValue;
				draft.currentSortBy = payload.sortBy;
				break;

			case actions.RECEIVED_CANDIDATES:
				const newCandidates = payload.candidates.reduce(
					(acc, cur) => ({
						...acc,
						[cur.rid]: {
							...cur,
							requestComplete: cur.complete_status === 1,
							references: [],
							loading: false,
							loadingReferences: false,
							cancellingReminders: false,
							sendingReminder: false,
							showMissingGapsWarning: cur?.required_range_missing > 0,
							showDigitalIdMissingError:
								cur?.can_yrequired === 1 &&
								cur?.can_yverified === '0000-00-00 00:00:00' &&
								cur.referees > 0,
						},
					}),
					{}
				);
				let candidatesById;

				if (window.TOUR_ACTIVE) {
					candidatesById = {
						...newCandidates,
						...dummyCandidate,
					};

					draft.activeCandidateId = 1;
					draft.activeReferenceId = null;
					draft.list = ['1', ...Object.keys(newCandidates)];
				} else {
					if (payload.start) {
						candidatesById = {
							...draft.byId,
							...newCandidates,
						};
					} else {
						candidatesById = newCandidates;
						draft.activeCandidateId = payload.activeCandidateId;
					}

					draft.list =
						payload.sortOrder === 'oldest'
							? sortByOldest(Object.keys(candidatesById))
							: sortByNewest(Object.keys(candidatesById));
				}

				draft.byId = candidatesById;
				draft.failedCandidatesRequest = null;
				draft.loadingCandidates = false;
				draft.loadingMoreCandidates = false;
				draft.hasMoreCandidates = payload.candidates.length >= 100;

				break;

			case actions.GET_CANDIDATE:
				draft.activeCandidateId = payload.candidateId;
				draft.byId[payload.candidateId] = {
					...draft.byId[payload.candidateId],
					loading: true,
				};
				draft.activeReferenceId = null;

				break;

			case actions.RECEIVED_CANDIDATE:
				const { candidate } = payload;

				draft.byId[candidate.rid] = {
					...draft.byId[candidate.rid],
					...candidate,
					requestComplete: candidate.complete_status === 1,
					loading: false,
					showMissingGapsWarning: candidate?.required_range_missing > 0,
					showDigitalIdMissingError:
						candidate?.can_yrequired === 1 &&
						candidate?.can_yverified === '0000-00-00 00:00:00' &&
						candidate?.referees > 0,
				};

				break;

			case actions.GET_CANDIDATE_REFERENCES:
				draft.byId[payload.candidateId] = {
					...draft.byId[payload.candidateId],
					loadingReferences: true,
				};

				break;

			case actions.RECEIVED_CANDIDATE_REFERENCES:
				draft.byId[payload.candidateId] = {
					...draft.byId[payload.candidateId],
					loadingReferences: false,
					references: payload.references,
				};

				break;

			case actions.SET_ACTIVE_REFERENCE:
				draft.activeReferenceId = payload.activeReferenceId;

				break;

			case actions.DELETE_CANDIDATE:
				draft.byId[payload.candidateId] = {
					...draft.byId[payload.candidateId],
					deleting: true,
				};

				break;

			case actions.CANDIDATE_DELETED:
				// Set deleting to false - the candidate will be removed when the list refreshes
				if (draft.byId[payload.candidateId]) {
					draft.byId[payload.candidateId].deleting = false;
				}
				break;

			case actions.CANDIDATE_DELETE_FAILED:
				draft.byId[payload.candidateId] = {
					...draft.byId[payload.candidateId],
					deleting: false,
				};

				break;

			case actions.ARCHIVE_CANDIDATE:
				draft.byId[payload.candidateId] = {
					...draft.byId[payload.candidateId],
					archiving: true,
				};

				break;

			case actions.CANDIDATE_ARCHIVED:
				// Set archiving to false - the candidate will be removed when the list refreshes
				if (draft.byId[payload.candidateId]) {
					draft.byId[payload.candidateId].archiving = false;
				}
				break;

			case actions.CANDIDATE_ARCHIVE_FAILED:
				draft.byId[payload.candidateId] = {
					...draft.byId[payload.candidateId],
					archiving: false,
				};

				break;

			case actions.UNARCHIVE_CANDIDATE:
				draft.byId[payload.candidateId] = {
					...draft.byId[payload.candidateId],
					unarchiving: true,
				};

				break;

			case actions.CANDIDATE_UNARCHIVED:
				if (draft.byId[payload.candidateId]) {
					draft.byId[payload.candidateId] = {
						...draft.byId[payload.candidateId],
						unarchiving: false,
						request_archived: 0,
					};
				}

				break;

			case actions.CANDIDATE_UNARCHIVE_FAILED:
				draft.byId[payload.candidateId] = {
					...draft.byId[payload.candidateId],
					unarchiving: false,
				};

				break;

			case actions.SET_CANCEL_REMINDER_LOADER:
				draft.byId[payload.candidateId] = {
					...draft.byId[payload.candidateId],
					cancellingReminders: payload.isCancelling,
				};

				break;

			case actions.SET_SEND_REMINDER_LOADER:
				draft.byId[payload.candidateId] = {
					...draft.byId[payload.candidateId],
					sendingReminder: payload.isSendingReminder,
				};

				break;

			case actions.REFERENCE_REQUEST_SUCCESS:
				draft.activeReferenceId = null;

				break;

			case actions.DELETE_REFERENCE:
				draft.byId[draft.activeCandidateId] = {
					...draft.byId[draft.activeCandidateId],
					deletingReference: true,
				};

				break;

			case actions.DELETE_REFERENCE_SUCCESS:
			case actions.DELETE_REFERENCE_FAILED:
				draft.byId[draft.activeCandidateId] = {
					...draft.byId[draft.activeCandidateId],
					deletingReference: false,
				};

				break;

			case actions.EDIT_NUM_OF_REFERENCES:
				draft.byId[draft.activeCandidateId] = {
					...draft.byId[draft.activeCandidateId],
					editingNumOfReferences: true,
				};

				break;

			case actions.EDIT_NUM_OF_REFERENCES_SUCCESS:
			case actions.EDIT_NUM_OF_REFERENCES_FAILED:
				draft.byId[draft.activeCandidateId] = {
					...draft.byId[draft.activeCandidateId],
					editingNumOfReferences: false,
				};

				break;

			case actions.POST_CANDIDATE_COMMENT:
				draft.byId[draft.activeCandidateId] = {
					...draft.byId[draft.activeCandidateId],
					postingComment: true,
				};

				break;

			case actions.POST_CANDIDATE_COMMENT_SUCCESS:
			case actions.POST_CANDIDATE_COMMENT_FAILED:
				draft.byId[draft.activeCandidateId] = {
					...draft.byId[draft.activeCandidateId],
					postingComment: false,
				};

				break;

			case actions.GET_FILTER_DATA:
				draft.loadingFilterData = true;

				break;

			case actions.RECEIVED_FILTER_DATA:
				draft.loadingFilterData = false;
				draft.filterData = {
					users: transformFilterData(payload.users),
					teams: transformFilterData(payload.teams),
					tags: transformTagsFilterData(payload.tags),
				};

				break;

			case actions.FILTER_DATA_ERROR:
				draft.loadingFilterData = false;
				draft.filterData = {
					users: [],
					teams: [],
					tags: [],
				};

				break;

			case 'SETUP_DUMMY_REQUEST':
				draft.byId = dummyCandidate;
				// draft.list = ['61836'];
				draft.activeCandidateId = 61836;
				draft.activeReferenceId = null;

				break;

			case actions.UPDATE_CANDIDATE_TAGS:
				draft.byId[payload.candidateId] = {
					...draft.byId[payload.candidateId],
					tags: payload.tags,
				};

				break;

			// Request Settings
			case actions.GET_REQUEST_SETTINGS:
				draft.requestSettings = {
					...draft.requestSettings,
					isLoading: true,
					error: null,
				};
				break;

			case actions.RECEIVED_REQUEST_SETTINGS:
				draft.requestSettings = {
					isLoading: false,
					isUpdating: false,
					error: null,
					data: payload,
				};
				break;

			case actions.ERROR_REQUEST_SETTINGS:
				draft.requestSettings = {
					...draft.requestSettings,
					isLoading: false,
					error: payload,
				};
				break;

			case actions.UPDATE_REQUEST_SETTINGS:
				draft.requestSettings = {
					...draft.requestSettings,
					isUpdating: true,
					error: null,
				};
				break;

			case actions.UPDATED_REQUEST_SETTINGS:
				draft.requestSettings = {
					...draft.requestSettings,
					isUpdating: false,
					error: null,
				};
				break;

			case actions.ERROR_UPDATING_REQUEST_SETTINGS:
				draft.requestSettings = {
					...draft.requestSettings,
					isUpdating: false,
					error: payload,
				};
				break;

			case actions.CLEAR_REQUEST_SETTINGS:
				draft.requestSettings = {
					isLoading: false,
					isUpdating: false,
					error: null,
					data: null,
				};
				break;

			case actions.SET_EDIT_REQUEST_MODAL_OPEN:
				draft.editRequestModalOpen = payload;
				break;

			// Referee Settings
			case actions.GET_REFEREE_SETTINGS:
				draft.refereeSettings = {
					...draft.refereeSettings,
					isLoading: true,
					error: null,
				};
				break;

			case actions.RECEIVED_REFEREE_SETTINGS:
				draft.refereeSettings = {
					isLoading: false,
					isUpdating: false,
					error: null,
					data: payload,
				};
				break;

			case actions.ERROR_REFEREE_SETTINGS:
				draft.refereeSettings = {
					...draft.refereeSettings,
					isLoading: false,
					error: payload,
				};
				break;

			case actions.UPDATE_REFEREE_SETTINGS:
				draft.refereeSettings = {
					...draft.refereeSettings,
					isUpdating: true,
					error: null,
				};
				break;

			case actions.UPDATED_REFEREE_SETTINGS:
				draft.refereeSettings = {
					...draft.refereeSettings,
					isUpdating: false,
					error: null,
				};
				break;

			case actions.ERROR_UPDATING_REFEREE_SETTINGS:
				draft.refereeSettings = {
					...draft.refereeSettings,
					isUpdating: false,
					error: payload,
				};
				break;

			case actions.CLEAR_REFEREE_SETTINGS:
				draft.refereeSettings = {
					isLoading: false,
					isUpdating: false,
					error: null,
					data: null,
				};
				break;

			case actions.SET_EDIT_REFEREE_MODAL_OPEN:
				draft.editRefereeModalOpen = payload.isOpen;
				draft.editRefereeModalRefereeId = payload.refereeId;
				break;
		}
	});
